import { createSelector } from 'reselect';
import i18n from 'i18next';

import {
	OrderAdditionalServiceGdsServiceServiceType,
	FlightSeatMapService_OrderAdditionalServiceGdsServiceSeat_Fragment,
	RowSeatFragment,
	MoneyFragment,
	FlightClass,
	SeatComfort,
	SeatMapRestrictions
} from '@websky/graphql';

import { Money as MoneyType } from '../../../../../Money/types';
import { CustomSeatComfort, CustomServiceClass, SeatMapComfort, SeatMapFlightClass, SeatMapService } from '../types';
import { getState } from '../rootSelector';
import { getSeatMapAdditionalServices } from '../additionalServices/selectors';
import { getIsFreeSeat } from '../../lib/getIsFreeSeat';
import { getSelectedSegmentId } from '../selectedSegmentId/selectors';

export const getSeatMapState = createSelector(getState, state => state.seatMap);

export const getSeatMapSegments = createSelector(getSeatMapState, seatMap => seatMap.segments);

export const getSeatMapSegmentsСount = createSelector(getSeatMapSegments, segments => segments.length);

export const getSelectedSegment = createSelector(
	getSeatMapSegments,
	getSelectedSegmentId,
	(segments, selectedSegmentId) => {
		return segments.find(segment => segment.segmentsInfo?.id === selectedSegmentId.toString());
	}
);

export const getSelectedSegmentFilters = createSelector(getSelectedSegment, segment => {
	const restrictions = new Set<SeatMapRestrictions>();

	segment.decks.forEach(deck =>
		deck.rows.forEach(row =>
			row.seats.forEach(seat => {
				if (seat.restrictions) {
					seat.restrictions.forEach(restriction => restrictions.add(restriction));
				}
			})
		)
	);

	return Array.from(restrictions);
});

export const getIsNextSegmentAvaliable = createSelector(
	getSelectedSegmentId,
	getSeatMapSegmentsСount,
	(selectedSegmentId, seatMapSegmentsСount) => selectedSegmentId < seatMapSegmentsСount - 1
);

export const getSelectedSegmentRows = createSelector(
	getSelectedSegment,
	selectedSegment => selectedSegment?.decks?.[0]?.rows ?? []
);

export const getSeatMapRestrictionsBySegmentId = createSelector(getSelectedSegmentRows, selectedSegmentRows => {
	const restrictionSet = new Set<SeatMapRestrictions>();

	selectedSegmentRows.forEach(row => {
		row.seats?.forEach(seat => {
			if (seat.restrictions?.length) {
				seat.restrictions.forEach(restriction => restrictionSet.add(restriction));
			}
		});
	});

	return [...restrictionSet];
});

export const getSeatMapServices = createSelector(getSeatMapAdditionalServices, seatMapAdditionalService => {
	return seatMapAdditionalService?.gdsServices?.services?.filter(
		service => service.type === OrderAdditionalServiceGdsServiceServiceType.Seat
	) as FlightSeatMapService_OrderAdditionalServiceGdsServiceSeat_Fragment[];
});

export const getIsWideSeatMap = createSelector(getSelectedSegmentRows, rowsBySegmentId => {
	return rowsBySegmentId?.some(row => (row!.seats?.filter(seat => !seat?.isAisle)?.length ?? 0) > 6);
});

export const getSeatMapServicesBySelectedSegmentId = createSelector(
	getSeatMapServices,
	getSelectedSegmentId,
	(services, selectedSegmentId) => {
		return services.filter(service =>
			service.allowedSegments?.some(segment => segment?.some(id => id === selectedSegmentId.toString()))
		);
	}
);

export const getServicesRfiscByClassAndComfort = createSelector(getSelectedSegmentRows, rowsBySegmentId => {
	// Seats by service class
	const seatsByServiceClassMap = new Map<SeatMapFlightClass, RowSeatFragment[]>();

	rowsBySegmentId?.forEach(row => {
		row.seats?.forEach(seat => {
			let serviceClass: SeatMapFlightClass = seat.seatService?.serviceClass ?? null;

			if (!serviceClass && seat && getIsFreeSeat(seat)) {
				serviceClass = CustomServiceClass.Free;
			}

			if (serviceClass) {
				seatsByServiceClassMap.set(serviceClass, [...(seatsByServiceClassMap.get(serviceClass) ?? []), seat]);
			}
		});
	});

	// Seat rfisc by service class and comfort
	const seatRfiscByServiceClassAndComfort = new Map<SeatMapFlightClass, Map<SeatMapComfort, Set<string>>>();

	seatsByServiceClassMap.forEach((seats, serviceClass) => {
		const seatRfiscByComfort = new Map<SeatMapComfort, Set<string>>();

		seats.forEach(seat => {
			let comfort: SeatMapComfort = seat.seatService?.comfort ?? null;

			if (!comfort && getIsFreeSeat(seat)) {
				comfort = CustomSeatComfort.Free;
			}

			if (comfort) {
				seatRfiscByComfort.set(comfort, new Set([...(seatRfiscByComfort.get(comfort) ?? []), seat.rfisc]));
			}
		});

		seatRfiscByServiceClassAndComfort.set(serviceClass, seatRfiscByComfort);
	});

	return seatRfiscByServiceClassAndComfort;
});

export const getServicesByClassAndComfort = createSelector(
	getSeatMapServicesBySelectedSegmentId,
	servicesBySelectedSegment => {
		// Services by service class
		const servicesByServiceClass = new Map<
			FlightClass,
			FlightSeatMapService_OrderAdditionalServiceGdsServiceSeat_Fragment[]
		>();

		servicesBySelectedSegment.forEach(service => {
			const serviceClass = service.serviceClass ?? null;

			servicesByServiceClass.set(serviceClass, [...(servicesByServiceClass.get(serviceClass) ?? []), service]);
		});

		// Services by services class and comfort
		const servicesByServiceClassAndComfort = new Map<
			FlightClass,
			Map<SeatComfort, FlightSeatMapService_OrderAdditionalServiceGdsServiceSeat_Fragment[]>
		>();

		servicesByServiceClass.forEach((servicesByServiceClass, serviceClass) => {
			const servicesByComfort = new Map<
				SeatComfort,
				FlightSeatMapService_OrderAdditionalServiceGdsServiceSeat_Fragment[]
			>();

			servicesByServiceClass.forEach(service => {
				const comfort = service.comfort ?? null;

				servicesByComfort.set(comfort, [...(servicesByComfort.get(comfort) ?? []), service]);
			});

			servicesByServiceClassAndComfort.set(serviceClass, servicesByComfort);
		});

		return servicesByServiceClassAndComfort;
	}
);

export const getSeatServiceGroups = createSelector(
	getServicesRfiscByClassAndComfort,
	getServicesByClassAndComfort,
	(servicesRfiscByClassAndComfort, servicesByClassAndComfort) => {
		const seatServices: SeatMapService[] = [];

		servicesByClassAndComfort.forEach((servicesByComfort, serviceClass) => {
			servicesByComfort.forEach((services, comfort) => {
				// Checking for the existence of seats for services
				if (servicesRfiscByClassAndComfort.get(serviceClass)?.has(comfort)) {
					const seatsRfisc = servicesRfiscByClassAndComfort?.get(serviceClass)?.get(comfort);

					let minPrice: MoneyType = null;
					let actualService: FlightSeatMapService_OrderAdditionalServiceGdsServiceSeat_Fragment = null;

					// Find min price and actual service
					services.forEach(service => {
						if (seatsRfisc?.has(service.rfisc)) {
							if (service.price?.amount && (!minPrice || minPrice?.amount > service.price.amount)) {
								minPrice = service.price;
							}

							if (!actualService || actualService.id < service.id) {
								actualService = service;
							}
						}
					});

					if (actualService !== null) {
						const { comfort: actualComfort, description } = actualService;

						seatServices.push({
							...actualService,
							description: i18n.t(description, { ns: 'SeatMap' }),
							serviceClass: actualService.serviceClass,
							// надо поправить потом для показа нескольких иконок в SeatInfo
							seats: [{ comfort: actualComfort }],
							price: minPrice,
							availableRfisc: [...(seatsRfisc ?? [])]
						});
					}
				}
			});
		});

		// Adding a service of free seats
		if (servicesRfiscByClassAndComfort.get(CustomServiceClass.Free)?.has(CustomSeatComfort.Free)) {
			seatServices.push({
				rfisc: '',
				name: i18n.t('Free seats', { ns: 'SeatMap' }),
				type: OrderAdditionalServiceGdsServiceServiceType.Seat,
				price: { amount: 0, currency: seatServices?.[0]?.price?.currency ?? null } as MoneyFragment,
				serviceClass: CustomServiceClass.Free,
				seats: [{ comfort: CustomSeatComfort.Free }],
				availableRfisc: []
			});
		}

		return seatServices;
	}
);
